import { HeartbeatRecorder } from "./heartbeats.js";
import { defaultStorage, type ArcadeStorage } from "./storage.js";
import PartySocket from "partysocket";

export interface ArcadeOptions {
  gameId: string;
  /** Base HTTPS endpoint of the API Worker, e.g. https://api.example.com */
  endpoint: string;
  storage?: ArcadeStorage;
  /** Optional Turnstile token provider, called for identity/session when configured. */
  turnstileToken?: () => string | Promise<string>;
  /** Optional display name used when minting an identity and syncing an existing one. */
  displayName?: string;
  /** SHA-256 Gravatar hash only; the email address never leaves the device. */
  avatarHash?: string;
  /** Maximum time for an HTTP request before it is aborted. Defaults to 15 seconds. */
  requestTimeoutMs?: number;
  /** HTTP transport override for Worker-runtime integration tests or custom runtimes. */
  fetcher?: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
}

export interface Session {
  sessionId: string;
  token: string;
  seed: string;
  mode: string;
}

export interface SubmitResult {
  status: "accepted" | "rejected" | "flagged";
  score: number;
  rank: number | null;
}

export interface BoardEntry {
  rank: number;
  playerId: string;
  displayName: string;
  score: number;
  createdAt: number;
}

export interface Board {
  top: BoardEntry[];
  me: { rank: number; score: number } | null;
}

export interface RoomInfo {
  roomId: string;
  gameId?: string;
  code?: string;
  wsUrl: string;
  token: string;
  waiting?: boolean;
}

export type RoomEvent = "state" | "message" | "player_joined" | "player_left" | "over" | "rejected" | "timeout";

export class ArcadeRoom {
  readonly socket: PartySocket;
  private readonly listeners = new Map<RoomEvent, Set<(...args: any[]) => void>>();

  constructor(readonly info: RoomInfo, endpoint: string) {
    const host = endpoint.replace(/^https?:\/\//, "").replace(/\/$/, "");
    this.socket = new PartySocket({ host, party: "room", room: info.roomId, query: { token: info.token } });
    this.socket.addEventListener("message", (event) => {
      try {
        const message = JSON.parse(String(event.data)) as Record<string, unknown>;
        const type = message.t as RoomEvent;
        this.emit("message", message);
        if (type === "state") this.emit("state", message.from, message.s ?? message.state, message);
        else if (type === "player_joined" || type === "player_left" || type === "over" || type === "rejected" || type === "timeout") this.emit(type, message);
      } catch {
        this.emit("rejected", { reason: "invalid_server_message" });
      }
    });
  }

  on(event: "state", listener: (from: string, state: unknown, message?: unknown) => void): () => void;
  on(event: Exclude<RoomEvent, "state">, listener: (...args: any[]) => void): () => void;
  on(event: RoomEvent, listener: (...args: any[]) => void): () => void {
    const listeners = this.listeners.get(event) ?? new Set();
    listeners.add(listener);
    this.listeners.set(event, listeners);
    return () => listeners.delete(listener);
  }

  send(message: Record<string, unknown>): void { this.socket.send(JSON.stringify(message)); }
  sendState(tick: number, state: unknown): void { this.send({ t: "state", tick, s: state }); }
  move(move: unknown, state: unknown): void { this.send({ t: "move", move, state }); }
  ping(id: string | number): void { this.send({ t: "ping", id }); }
  close(): void { this.socket.close(); }

  private emit(event: RoomEvent, ...args: any[]): void { for (const listener of this.listeners.get(event) ?? []) listener(...args); }
}

export class ArcadeError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(`arcade ${status}: ${code}`);
    this.name = "ArcadeError";
  }
}

export class ArcadeRequestTimeoutError extends Error {
  readonly code = "request_timeout";

  constructor(readonly timeoutMs: number) {
    super(`The game server did not respond within ${Math.ceil(timeoutMs / 1000)} seconds.`);
    this.name = "ArcadeRequestTimeoutError";
  }
}

/**
 * Framework-agnostic Arcade client (DESIGN.md §10). Owns identity persistence, token refresh,
 * session lifecycle, heartbeat trails, and generic realtime/turn-based rooms.
 */
export class Arcade {
  private readonly storage: ArcadeStorage;
  private readonly base: string;
  private playerId?: string;
  private deviceSecret?: string;
  private jwt?: string;
  private jwtExpMs = 0;
  private readonly recorders = new Map<string, HeartbeatRecorder>();

  constructor(private readonly opts: ArcadeOptions) {
    this.storage = opts.storage ?? defaultStorage();
    this.base = opts.endpoint.replace(/\/$/, "");
  }

  private key(k: string): string {
    return `arcade:${this.opts.gameId}:${k}`;
  }

  /** Mint/restore identity and obtain an access token. Call once before anything else. */
  async ready(): Promise<void> {
    this.playerId = (await this.storage.get(this.key("playerId"))) ?? undefined;
    this.deviceSecret = (await this.storage.get(this.key("deviceSecret"))) ?? undefined;

    if (!this.playerId || !this.deviceSecret) {
      const id = await this.request<{ playerId: string; deviceSecret: string }>(
        "POST",
        "/identity",
        await this.withTurnstile(this.opts.displayName ? { displayName: this.opts.displayName, ...(this.opts.avatarHash ? { avatarHash: this.opts.avatarHash } : {}) } : {}),
      );
      this.playerId = id.playerId;
      this.deviceSecret = id.deviceSecret;
      await this.storage.set(this.key("playerId"), this.playerId);
      await this.storage.set(this.key("deviceSecret"), this.deviceSecret);
    }
    await this.authenticate();
    if (this.opts.displayName) {
      await this.request("POST", "/identity/display-name", { displayName: this.opts.displayName, ...(this.opts.avatarHash ? { avatarHash: this.opts.avatarHash } : {}) }, await this.token());
    }
  }

  /** Update public profile metadata for an existing device identity. */
  async syncProfile(displayName: string, avatarHash?: string): Promise<void> {
    await this.request("POST", "/identity/display-name", { displayName, ...(avatarHash ? { avatarHash } : {}) }, await this.token());
  }

  /** The authenticated player id. Throws if `ready()` hasn't run. */
  get me(): string {
    if (!this.playerId) throw new Error("Arcade: call ready() first");
    return this.playerId;
  }

  private async authenticate(): Promise<void> {
    const r = await this.request<{ accessJwt: string; expiresAt: number }>(
      "POST",
      "/auth",
      { playerId: this.playerId, deviceSecret: this.deviceSecret },
    );
    this.jwt = r.accessJwt;
    this.jwtExpMs = r.expiresAt * 1000;
  }

  /** A valid access token, refreshing shortly before expiry. */
  async token(): Promise<string> {
    if (!this.jwt || Date.now() > this.jwtExpMs - 30_000) await this.authenticate();
    return this.jwt!;
  }

  /** Start a verified session; the server chooses the seed. */
  async startSession(mode: string): Promise<{ seed: string; session: Session }> {
    const r = await this.request<{ sessionId: string; seed: string; token: string }>(
      "POST",
      "/session",
      await this.withTurnstile({ gameId: this.opts.gameId, mode }),
      await this.token(),
    );
    const session: Session = { sessionId: r.sessionId, token: r.token, seed: r.seed, mode };
    return { seed: r.seed, session };
  }

  /** Begin recording signed heartbeats for a session. Pulse `.tick(score)` during play. */
  heartbeats(session: Session, cadenceMs?: number): HeartbeatRecorder {
    const rec = new HeartbeatRecorder(session.token, session.sessionId, cadenceMs);
    this.recorders.set(session.sessionId, rec);
    return rec;
  }

  /** Submit the final score; the SDK attaches the heartbeat trail if one was recorded. */
  async submitScore(session: Session, claimedScore: number): Promise<SubmitResult> {
    const rec = this.recorders.get(session.sessionId);
    const heartbeats = rec ? await rec.build(claimedScore) : [];
    this.recorders.delete(session.sessionId);
    return this.request<SubmitResult>(
      "POST",
      "/score",
      { sessionId: session.sessionId, token: session.token, claimedScore, heartbeats, ...(this.opts.turnstileToken ? { turnstileToken: await this.opts.turnstileToken() } : {}) },
      await this.token(),
    );
  }

  async createRoom(options: { mode: string; visibility?: "private" | "public"; kind?: "realtime" | "turn"; playerCount?: number }): Promise<ArcadeRoom> {
    const info = await this.request<RoomInfo>("POST", "/rooms", {
      gameId: this.opts.gameId,
      mode: options.mode,
      visibility: options.visibility ?? "private",
      ...(options.kind ? { kind: options.kind } : {}),
      ...(options.playerCount ? { playerCount: options.playerCount } : {}),
    }, await this.token());
    return new ArcadeRoom(info, this.base);
  }

  async joinRoom(code: string, gameId?: string): Promise<ArcadeRoom> {
    const info = await this.request<RoomInfo>("POST", "/rooms/join", { ...(gameId ? { gameId } : {}), code }, await this.token());
    return new ArcadeRoom(info, this.base);
  }

  async deleteRoom(roomId: string, token: string): Promise<void> {
    await this.request("DELETE", `/rooms/${encodeURIComponent(roomId)}`, undefined, await this.token(), {
      "X-Room-Token": token,
    });
  }

  async quickPlay(mode: string, kind?: "realtime" | "turn"): Promise<ArcadeRoom> {
    const info = await this.request<RoomInfo>("POST", "/rooms/quickplay", {
      gameId: this.opts.gameId,
      mode,
      ...(kind ? { kind } : {}),
    }, await this.token());
    return new ArcadeRoom(info, this.base);
  }

  async getTurnState(roomId: string, token: string): Promise<{ state: unknown; turn: string | null; deadline: number | null; history: unknown[] }> {
    return this.request("GET", `/rooms/${encodeURIComponent(roomId)}/state?token=${encodeURIComponent(token)}`, undefined, await this.token());
  }

  async submitMove(roomId: string, token: string, move: unknown, state: unknown): Promise<unknown> {
    return this.request("POST", `/rooms/${encodeURIComponent(roomId)}/move`, { token, move, state }, await this.token());
  }

  async nudgeOpponent(roomId: string, token: string): Promise<void> {
    await this.request("POST", `/rooms/${encodeURIComponent(roomId)}/nudge`, { token }, await this.token());
  }

  async getAcquireState(roomId: string, token: string): Promise<unknown> {
    return this.request("GET", `/rooms/${encodeURIComponent(roomId)}/state?token=${encodeURIComponent(token)}`, undefined, await this.token());
  }

  /** Read a redacted server-authoritative game view. Hidden deck state never crosses this API. */
  async getLunaGenesisState(roomId: string, token: string): Promise<unknown> {
    return this.request("GET", `/rooms/${encodeURIComponent(roomId)}/state?token=${encodeURIComponent(token)}`, undefined, await this.token());
  }

  async getRoomParticipants(roomId: string, token: string): Promise<{ participants: Array<{ playerId: string; displayName: string; avatarHash: string | null }> }> {
    return this.request("GET", `/rooms/${encodeURIComponent(roomId)}/participants?token=${encodeURIComponent(token)}`, undefined, await this.token());
  }

  async submitAcquireCommand(roomId: string, token: string, command: unknown): Promise<unknown> {
    return this.request("POST", `/rooms/${encodeURIComponent(roomId)}/acquire-command`, { token, command }, await this.token());
  }

  /** Submit one atomic placement + optional claim command. */
  async submitLunaGenesisCommand(roomId: string, token: string, command: unknown): Promise<unknown> {
    return this.request("POST", `/rooms/${encodeURIComponent(roomId)}/commands`, { token, command }, await this.token());
  }

  async myActiveMatches(): Promise<Array<{ roomId: string; myTurn: boolean; deadline: number | null }>> {
    const result = await this.request<{ active: Array<{ roomId: string; myTurn: boolean; deadline: number | null }> }>("GET", "/me/matches", undefined, await this.token());
    return result.active;
  }

  /** Read a leaderboard. `around: "me"` includes the caller's rank. */
  async leaderboard(
    mode: string,
    opts: { limit?: number; around?: string } = {},
  ): Promise<Board> {
    const qs = new URLSearchParams();
    if (opts.limit) qs.set("limit", String(opts.limit));
    if (opts.around) qs.set("around", opts.around);
    const path = `/board/${encodeURIComponent(this.opts.gameId)}/${encodeURIComponent(mode)}`;
    return this.request<Board>("GET", `${path}?${qs.toString()}`, undefined, await this.token());
  }

  private async withTurnstile(
    base: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    if (!this.opts.turnstileToken) return base;
    return { ...base, turnstileToken: await this.opts.turnstileToken() };
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    jwt?: string,
    extraHeaders: Record<string, string> = {},
  ): Promise<T> {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (jwt) headers["Authorization"] = `Bearer ${jwt}`;
    Object.assign(headers, extraHeaders);
    const timeoutMs = Math.max(1, this.opts.requestTimeoutMs ?? 15_000);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await (this.opts.fetcher ?? globalThis.fetch)(`${this.base}${path}`, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });
      const text = await res.text();
      const data = text ? JSON.parse(text) : {};
      if (!res.ok) throw new ArcadeError(res.status, (data as { error?: string }).error ?? "error");
      return data as T;
    } catch (cause) {
      if (controller.signal.aborted) throw new ArcadeRequestTimeoutError(timeoutMs);
      throw cause;
    } finally {
      clearTimeout(timeout);
    }
  }
}
